import { UseQueryResult } from "@tanstack/react-query";
import { SectorSummary } from "@/types/operations";
import { useEffect, useMemo } from "react";
import {
    getKeyValue,
    Spinner,
    Table,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
} from "@heroui/react";
import { TableBody } from "@heroui/react";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";
import useMap from "@/lib/map/useMap";
import {
    SECTOR_SUMMARY_FILL_LAYER,
    SECTOR_SUMMARY_LINE_LAYER,
    SECTOR_SUMMARY_POINT_LAYER,
    SECTOR_SUMMARY_SOURCE,
} from "@/lib/map/layers";

const COLUMNS = [
    {
        label: "Sector",
        key: "sector_number",
    },
    {
        label: "Start",
        key: "start_degree",
    },
    {
        label: "Stop",
        key: "stop_degree",
    },
    {
        label: "Rate",
        key: "n_rate",
    },
];

type Color = [number, number, number]; // [R, G, B]

function interpolateColor(color1: Color, color2: Color, factor: number): Color {
    const result: Color = [0, 0, 0];
    for (let i = 0; i < 3; i++) {
        result[i] = Math.round(color1[i] + factor * (color2[i] - color1[i]));
    }
    return result;
}

function rgbToHex(rgb: Color): string {
    return (
        "#" +
        rgb
            .map((x) => {
                const hex = x.toString(16);
                return hex.length === 1 ? "0" + hex : hex;
            })
            .join("")
    );
}

function generateColorBarValue(
    value: number,
    min: number,
    max: number,
    color1: Color,
    color2: Color,
    color3: Color,
): string {
    if (value < min || value > max) {
        throw new Error("Value is outside of min/max range.");
    }

    const range = max - min;
    const normalizedValue = (value - min) / range;

    let interpolatedRgb: Color;
    if (normalizedValue <= 0.5) {
        const factor = normalizedValue * 2;
        interpolatedRgb = interpolateColor(color1, color2, factor);
    } else {
        const factor = (normalizedValue - 0.5) * 2;
        interpolatedRgb = interpolateColor(color2, color3, factor);
    }

    return rgbToHex(interpolatedRgb);
}

function SectorTable({
    sectorQuery,
}: {
    sectorQuery: UseQueryResult<SectorSummary[]>;
}) {
    const columns = useMemo(() => {
        const cols = COLUMNS;
        if (
            sectorQuery?.data?.[0]?.sectors?.some(
                (sector) => sector.properties?.treatment,
            )
        ) {
            cols.push({ label: "Treatment", key: "treatment" });
        }
        return cols;
    }, [sectorQuery?.data]);

    const sectors = useMemo(() => {
        return (
            sectorQuery?.data?.[0]?.sectors?.map((sector, i) => ({
                sector_number: i + 1,
                start_degree: sector.properties.start,
                stop_degree: sector.properties.stop,
                n_rate: sector.properties.mean,
                treatment: sector.properties?.treatment,
            })) ?? []
        );
    }, [sectorQuery?.data]);

    const { map, mapLoaded } = useMap();

    useEffect(() => {
        if (!map || !mapLoaded) return;
        if (sectorQuery?.data?.[0]?.sectors?.length) {
            const min = sectorQuery?.data?.[0]?.sectors?.reduce(
                (min, sector) => Math.min(min, sector.properties.mean),
                Infinity,
            );
            const max = sectorQuery?.data?.[0]?.sectors?.reduce(
                (max, sector) => Math.max(max, sector.properties.mean),
                -Infinity,
            );
            addUpdateGeojsonSource(map, SECTOR_SUMMARY_SOURCE, {
                type: "geojson",
                tolerance: 0,
                data: {
                    type: "FeatureCollection",
                    features:
                        sectorQuery?.data?.[0]?.sectors?.map((feature) => {
                            return {
                                ...feature,
                                properties: {
                                    ...feature.properties,
                                    "fill-color": generateColorBarValue(
                                        feature.properties.mean,
                                        min,
                                        max,
                                        [255, 0, 0],
                                        [255, 255, 0],
                                        [0, 255, 0],
                                    ),
                                },
                            };
                        }) ?? [],
                },
            });
            addUpdateGeojsonLayers(map, [
                SECTOR_SUMMARY_FILL_LAYER,
                SECTOR_SUMMARY_LINE_LAYER,
                SECTOR_SUMMARY_POINT_LAYER,
            ]);
        }

        return () => {
            removeLayersIfExists(map, [
                SECTOR_SUMMARY_FILL_LAYER.id,
                SECTOR_SUMMARY_LINE_LAYER.id,
                SECTOR_SUMMARY_POINT_LAYER.id,
            ]);
            removeSourcesIfExists(map, [SECTOR_SUMMARY_SOURCE]);
        };
    }, [map, mapLoaded, sectorQuery?.data]);

    return (
        <Table
            isHeaderSticky
            isVirtualized
            maxTableHeight={500}
            aria-label="trendline"
            classNames={{
                th: "px-[0.25rem]",
                td: "px-[0.25rem]",
                wrapper: "p-0 shadow-none rounded-none",
            }}
        >
            <TableHeader columns={columns}>
                {(column) => (
                    <TableColumn key={column.key} allowsSorting>
                        {column.label}
                    </TableColumn>
                )}
            </TableHeader>
            <TableBody
                items={sectors}
                emptyContent={"No sectors found"}
                isLoading={sectorQuery.isFetching}
                loadingContent={<Spinner label="Loading..." />}
            >
                {(item) => (
                    <TableRow
                        key={item.sector_number}
                        className="hover:bg-gray-100 rounded-lg"
                    >
                        {(columnKey) => {
                            return (
                                <TableCell className="text-xs">
                                    {getKeyValue(item, columnKey) ?? "--"}
                                </TableCell>
                            );
                        }}
                    </TableRow>
                )}
            </TableBody>
        </Table>
    );
}

export default SectorTable;
